/** 登录页：Google OAuth 一键登录 + 邮箱密码表单。dsw design tokens。 */
import { useState, type FormEvent } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button } from '../../components/Button'
import { Card } from '../../components/Card'
import { Input } from '../../components/Input'
import { useAuth, consumeUnauthorizedFlag } from '../../auth/AuthContext'
import { consumePostLoginRedirect } from '../../auth/postLoginRedirect'
import css from './LoginPage.module.css'

function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
      <path
        fill="#4285F4"
        d="M23.5 12.3c0-.9-.1-1.5-.3-2.3H12v4.5h6.5c-.1 1.1-.8 2.7-2.4 3.8l-.1.1 3.5 2.7.2.1c2.2-2 3.8-5 3.8-8.9z"
      />
      <path
        fill="#34A853"
        d="M12 24c3.2 0 6-1.1 7.9-2.9l-3.8-2.9c-1 .7-2.4 1.2-4.1 1.2-3.1 0-5.8-2.1-6.8-5l-.1.1-3.7 2.9v.1C3.5 21.3 7.5 24 12 24z"
      />
      <path
        fill="#FBBC05"
        d="M5.2 14.4c-.2-.7-.4-1.5-.4-2.4s.1-1.7.4-2.4l-.1-.1-3.7-2.9-.1.1C.5 8.2 0 10 0 12s.5 3.8 1.3 5.4l3.9-3z"
      />
      <path
        fill="#EA4335"
        d="M12 4.7c1.8 0 3 .8 3.7 1.4l3.3-3.2C17.9 1.1 15.2 0 12 0 7.5 0 3.5 2.7 1.3 6.6l3.9 3c1-2.9 3.7-4.9 6.8-4.9z"
      />
    </svg>
  )
}

export function LoginPage() {
  const navigate = useNavigate()
  const { configured, signInWithGoogle, signInWithEmail, signUpWithEmail } = useAuth()
  const [mode, setMode] = useState<'signin' | 'signup'>('signin')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [invalidUser] = useState(() => consumeUnauthorizedFlag())

  const submit = async (e: FormEvent) => {
    e.preventDefault()
    setError(null)
    setBusy(true)
    try {
      const fn = mode === 'signin' ? signInWithEmail : signUpWithEmail
      const err = await fn(email.trim(), password)
      if (err) {
        setError(err)
      } else if (!consumePostLoginRedirect()) {
        // 没有暂存路由时保持原有行为：登录后进资产总览
        navigate('/', { replace: true })
      }
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className={css.page}>
      <Card className={css.card}>
        <h1 className={css.title}>统一控制台</h1>
        <p className={css.subtitle}>登录后继续</p>

        {!configured && (
          <p className={css.warning}>
            认证未配置：请在构建时设置 VITE_SUPABASE_URL / VITE_SUPABASE_ANON_KEY
          </p>
        )}

        {invalidUser && (
          <p className={css.warning}>
            无效用户：该账号不在访问白名单内，请使用授权邮箱登录
          </p>
        )}

        <Button
          variant="outline"
          size="md"
          className={css.googleBtn}
          icon={<GoogleIcon />}
          disabled={!configured || busy}
          onClick={() => void signInWithGoogle()}
        >
          使用 Google 登录
        </Button>

        <div className={css.divider}>
          <span>或</span>
        </div>

        <form className={css.form} onSubmit={(e) => void submit(e)}>
          <Input
            type="email"
            placeholder="邮箱"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            autoComplete="email"
            aria-label="邮箱"
          />
          <Input
            type="password"
            placeholder="密码"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            minLength={6}
            autoComplete={mode === 'signin' ? 'current-password' : 'new-password'}
            aria-label="密码"
          />
          {error && <p className={css.error} role="alert">{error}</p>}
          <Button type="submit" variant="primary" size="md" disabled={!configured || busy}>
            {busy ? '处理中…' : mode === 'signin' ? '登录' : '注册'}
          </Button>
        </form>

        <button
          type="button"
          className={css.switchMode}
          onClick={() => {
            setMode(mode === 'signin' ? 'signup' : 'signin')
            setError(null)
          }}
        >
          {mode === 'signin' ? '没有账号？去注册' : '已有账号？去登录'}
        </button>
      </Card>
    </div>
  )
}

/**
 * 认证状态解析中的登录页骨架：布局与登录卡片一致，避免首屏白屏。
 * 只在 `/login` 且 AuthProvider 仍在 getSession() 时渲染。
 */
export function LoginPageSkeleton() {
  return (
    <div className={css.page} role="status" aria-live="polite" aria-label="正在确认登录状态…">
      <div className={`${css.card} ${css.skeletonCard}`}>
        <span className={css.skeletonTitle} />
        <span className={css.skeletonSubtitle} />
        <span className={css.skeletonButton} />
        <span className={css.skeletonDivider} />
        <span className={css.skeletonInput} />
        <span className={css.skeletonInput} />
        <span className={css.skeletonButton} />
      </div>
    </div>
  )
}

